import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AuthContext } from "../../../contexts/AuthContext";
import { useSuccessDialog } from "../../../contexts/SuccessDialogContext";
import ComponentLoading from "../../App/ComponentLoading";
import IRASDashboard from "./IRASDashboard";
import { IRAS_CONFIG } from "../../../config/irasConfig";
import {
  clearIrasOAuthReturnContext,
  readIrasOAuthReturnCompany,
  removePersistentValue,
} from "./irasOAuthHelpers";

const {
  TOKEN_STORAGE_KEY,
  STATE_STORAGE_KEY,
  SERVER_OAUTH_CONNECTED_KEY,
  SERVER_OAUTH_EXPIRES_AT_KEY,
} = IRAS_CONFIG.SECURITY_CONFIG;

const removeOauthQueryParams = (search) => {
  const params = new URLSearchParams(search);
  ["code", "state", "error", "error_description"].forEach((key) => {
    params.delete(key);
  });
  const nextSearch = params.toString();
  return nextSearch ? `?${nextSearch}` : "";
};

const getNestedValue = (source, path) =>
  path.reduce((value, key) => (value == null ? undefined : value[key]), source);

const getIrasErrorResponse = (error) =>
  error?.response?.data || error?.error?.response?.data || error;

const getIrasErrorReason = (error) => {
  const body = getIrasErrorResponse(error);
  return (
    getNestedValue(body, ["errors", "reason"]) ||
    getNestedValue(body, ["data", "reason"]) ||
    body?.reason
  );
};

const getIrasErrorStatus = (error) =>
  error?.response?.status ||
  error?.error?.response?.status ||
  error?.statusCode;

const getIrasErrorMessage = (error) => {
  if (
    getIrasErrorStatus(error) === 409 &&
    getIrasErrorReason(error) === "exchange_in_progress"
  ) {
    return "Another user is currently connecting this company to IRAS. Please try again shortly.";
  }

  const body = getIrasErrorResponse(error);
  return (
    body?.message ||
    error?.message ||
    "Unable to verify the IRAS CorpPass connection. Please try again."
  );
};

const getStoredReturnCompanyUen = () => {
  const parsed = readIrasOAuthReturnCompany();
  return parsed?.uen ? `${parsed.uen}`.trim() : "";
};

const getCompanyUen = (company) =>
  company?.uen ? `${company.uen}`.trim() : getStoredReturnCompanyUen();

const formatConnectionSuccessMessage = (response) =>
  response?.reused_existing_token
    ? "IRAS CorpPass connection is ready.\nAn existing active token was reused for this company."
    : "IRAS CorpPass connected successfully.\nA new IRAS connection was created for this company.";

const getLocationState = (state, authError) => {
  const nextState = { ...(state || {}) };
  if (authError) {
    nextState.irasAuthError = authError;
  } else {
    delete nextState.irasAuthError;
  }
  return nextState;
};

const CorrespondencesTab = ({
  company,
  getTaxComputationList,
  onBackToTaxComputation,
}) => {
  const { exchangeCorpPassToken, validateIrasToken } = useContext(AuthContext);
  const handleSuccess = useSuccessDialog();
  const location = useLocation();
  const navigate = useNavigate();

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState("");
  const authCheckIdRef = useRef(0);
  const oauthExchangeRef = useRef({ key: "", promise: null });

  const clearIrasSession = useCallback(() => {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    removePersistentValue(STATE_STORAGE_KEY);
    sessionStorage.removeItem(SERVER_OAUTH_CONNECTED_KEY);
    sessionStorage.removeItem(SERVER_OAUTH_EXPIRES_AT_KEY);
  }, []);

  const validateStoredToken = useCallback(async () => {
    const connected = sessionStorage.getItem(SERVER_OAUTH_CONNECTED_KEY);
    const expRaw = sessionStorage.getItem(SERVER_OAUTH_EXPIRES_AT_KEY);
    if (connected === "1" && expRaw) {
      const exp = new Date(expRaw);
      if (!Number.isNaN(exp.getTime()) && exp > new Date()) {
        return true;
      }
      clearIrasSession();
      return false;
    }

    const irasToken = localStorage.getItem(TOKEN_STORAGE_KEY);

    if (!irasToken) {
      return false;
    }

    const response = await validateIrasToken(irasToken);
    const isValid =
      response?.status === "success" && response?.data?.valid === true;

    if (!isValid) {
      clearIrasSession();
    }

    return isValid;
  }, [clearIrasSession, validateIrasToken]);

  const handleOauthCallback = useCallback(
    async (code, state, error, errorDescription) => {
      if (error) {
        throw new Error(
          errorDescription ||
            `CorpPass authentication was not completed: ${error}`
        );
      }

      if (!code || !state) {
        throw new Error("Missing CorpPass authorisation code or state.");
      }

      const redirectUri = `${window.location.origin}${location.pathname}`;
      const uen = getCompanyUen(company);
      if (!uen) {
        throw new Error("Company UEN is required to complete IRAS connection.");
      }

      const response = await exchangeCorpPassToken(
        code,
        state,
        redirectUri,
        uen
      );

      if (response?.status !== "success") {
        throw new Error(response?.message || "Unable to exchange IRAS token.");
      }

      clearIrasOAuthReturnContext();
      handleSuccess(formatConnectionSuccessMessage(response));
      navigate(
        {
          pathname: location.pathname,
          search: removeOauthQueryParams(location.search),
          hash: location.hash,
        },
        { replace: true, state: getLocationState(location.state) }
      );

      return true;
    },
    [company, exchangeCorpPassToken, handleSuccess, location, navigate]
  );

  const exchangeOauthCallbackOnce = useCallback(
    (code, state, error, errorDescription) => {
      const key = JSON.stringify([
        location.pathname,
        code || "",
        state || "",
        error || "",
        errorDescription || "",
        getCompanyUen(company) || "",
      ]);

      if (
        oauthExchangeRef.current.key === key &&
        oauthExchangeRef.current.promise
      ) {
        return oauthExchangeRef.current.promise;
      }

      const promise = handleOauthCallback(
        code,
        state,
        error,
        errorDescription
      ).finally(() => {
        if (oauthExchangeRef.current.key === key) {
          oauthExchangeRef.current.promise = null;
        }
      });

      oauthExchangeRef.current = { key, promise };
      return promise;
    },
    [company, handleOauthCallback, location.pathname]
  );

  const removeCallbackFromUrl = useCallback(
    (errorMessage) => {
      navigate(
        {
          pathname: location.pathname,
          search: removeOauthQueryParams(location.search),
          hash: location.hash,
        },
        {
          replace: true,
          state: getLocationState(location.state, errorMessage),
        }
      );
    },
    [location, navigate]
  );

  const checkIrasAccess = useCallback(async () => {
    const checkId = authCheckIdRef.current + 1;
    authCheckIdRef.current = checkId;
    const isCurrentCheck = () => authCheckIdRef.current === checkId;
    let hasOauthCallback = false;

    try {
      setIsLoading(true);
      setAuthError(location.state?.irasAuthError || "");

      const params = new URLSearchParams(location.search);
      const code = params.get("code");
      const state = params.get("state");
      const error = params.get("error");
      const errorDescription = params.get("error_description");
      hasOauthCallback = Boolean(code || state || error);

      if (hasOauthCallback) {
        await exchangeOauthCallbackOnce(code, state, error, errorDescription);
        if (isCurrentCheck()) {
          setIsAuthenticated(true);
        }
        return;
      }

      const isValid = await validateStoredToken();
      if (isCurrentCheck()) {
        setIsAuthenticated(isValid);
      }
    } catch (error) {
      if (!isCurrentCheck()) {
        return;
      }
      console.error("IRAS access check failed:", error);
      const errorMessage = getIrasErrorMessage(error);
      setIsAuthenticated(false);
      clearIrasSession();
      setAuthError(errorMessage);
      if (hasOauthCallback) {
        removeCallbackFromUrl(errorMessage);
      }
    } finally {
      if (isCurrentCheck()) {
        setIsLoading(false);
      }
    }
  }, [
    clearIrasSession,
    exchangeOauthCallbackOnce,
    location.search,
    location.state?.irasAuthError,
    removeCallbackFromUrl,
    validateStoredToken,
  ]);

  useEffect(() => {
    checkIrasAccess();
  }, [checkIrasAccess]);

  const currentParams = new URLSearchParams(location.search);
  const hasOauthCallback = Boolean(
    currentParams.get("code") ||
      currentParams.get("state") ||
      currentParams.get("error")
  );

  if (isLoading && hasOauthCallback) {
    return <ComponentLoading message='Checking IRAS authentication...' />;
  }

  return (
    <IRASDashboard
      company={company}
      isAuthenticated={isAuthenticated}
      isAuthChecking={isLoading}
      authError={authError}
      getTaxComputationList={getTaxComputationList}
      onDisconnect={() => setIsAuthenticated(false)}
      onBackToTaxComputation={onBackToTaxComputation}
    />
  );
};

export default CorrespondencesTab;
